'use client';

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchBlogs } from '../api';
import dateIcon from '../../assets/images/date.png';
import authorIcon from '../../assets/images/user.png';
import { useTranslation } from 'react-i18next';
import { text } from 'node:stream/consumers';
import { left } from '@popperjs/core/lib/enums';

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL!;

interface BlogItem {
  _id: string;
  image: string;
  date: string;
  author: string;
  title: string;
  description: string;
  englishTitle: string;
}

const titleToSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\u0600-\u06FF]+/g, '-') // Support English + Arabic slugs
    .replace(/^-+|-+$/g, '');
};

const Blogs: React.FC = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<BlogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language === 'ar' ? 'ar' : 'en';

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const data = await fetchBlogs();

        const activeBlogs = data.blogs
          .filter((b: any) => b.isActive && b.isHomeVisible === true)
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .slice(0, 2)
          .map((b: any) => ({
            _id: b._id,
            image: `${IMAGE_BASE_URL}/${b.image}`,
            date: new Date(b.createdAt).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
            author: 'Pritesh Shah',
            title: b.serviceTitle?.[currentLang] || b.serviceTitle?.en || '',
            englishTitle: b.serviceTitle?.en || '',
            description:
              b.description?.[currentLang] || b.description?.en || '',
          }));

        setBlogs(activeBlogs);
        setLoading(false);
      } catch (err) {
        setError(t('failed_to_load_blogs') || 'Failed to load blogs');
        setLoading(false);
      }
    };

    loadBlogs();
  }, [i18n.language, t]);

  const handleBlogClick = (englishTitle: string) => {
    navigate(`/blog/${titleToSlug(englishTitle)}`);
  };

  if (loading) {
    return (
      <div className="container text-center my-5">
        <div className="main-title">{/* <h2>{t('loading_blogs')}</h2> */}</div>
      </div>
    );
  }

  if (blogs.length === 0) {
    return (
      <div className="container text-center my-5">
        <div className="main-title">
          <h2>{t('no_blogs_available')}</h2>
        </div>
      </div>
    );
  }

  return (
    <section className="blog-news">
      <div className="container">
        <div className="row">
          <div className="col-md-12 text-center">
            <div className="main-title content_wrapper">
              <h2>{t('blog_news')}</h2>
            </div>
          </div>
        </div>

        <div className="row mt-5">
          {blogs.map((blog) => (
            <div className="col-md-6 mb-4 box_wraper" key={blog._id}>
              <div
                className="blogg_box"
                onClick={() => handleBlogClick(blog.englishTitle)}
                style={{ cursor: 'pointer' }}
              >
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="img-fluid w-100 mb-3"
                />

                <div
                  className="auther_info d-flex align-items-center gap-3 mb-2"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '32px',
                    padding: '20px',
                  }}
                >
                  <div className="date d-flex align-items-center gap-1">
                    <img
                      src={dateIcon}
                      style={{ width: '20px', height: '20px' }}
                      alt="Date"
                    />
                    <span>{blog.date}</span>
                  </div>
                  <div className="auther_name d-flex align-items-center gap-1">
                    <img
                      src={authorIcon}
                      style={{ width: '20px', height: '20px' }}
                      alt="Author"
                    />
                    <span>{t('author')}</span>
                  </div>
                </div>

                <div
                  className=""
                  style={{
                    textAlign: 'left',
                    padding: '17px',
                    position: 'relative',
                    zIndex: 1,
                    background: '#db2217',
                    borderRadius: '0px 110px 0px 0px',
                    height: '110px',
                  }}
                >
                  <h4
                    style={{
                      color: '#fff',
                      marginLeft: '10px',
                      fontSize: '24px',
                      fontWeight: 600,
                    }}
                  >
                    {blog.title.length > 40
                      ? blog.title.slice(0, 40) + '...'
                      : blog.title}
                  </h4>
                  <p
                    style={{
                      color: '#fff',
                      marginLeft: '10px',
                      marginBottom: 0,
                    }}
                  >
                    {blog.description.length > 70
                      ? blog.description.slice(0, 70) + '...'
                      : blog.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="row pt-4">
          <div className="col-md-12 text-center">
            <div className="button_text" style={{}}>
              <Link to="/blog">{t('view_more')}</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Blogs;
